'use client'

import { useEffect, useState } from 'react'
import { AlertCircle, ArrowLeft, Loader2, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ContentPage } from '@/components/content-page'
import {
  ForumAttachmentPicker,
  type ForumAttachmentInput,
} from '@/components/forum-attachment-picker'
import { ForumFilePicker, type ForumFileInput } from '@/components/forum-file-picker'
import { forumCategories, forumTags } from '@/lib/forum-options'

type User = {
  id: number
  email: string
  handle: string | null
  avatarPath: string | null
  disclaimerAccepted: boolean
  isAdmin: boolean
}

export default function NewTopicPage() {
  const [user, setUser] = useState<User | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [category, setCategory] = useState('help')
  const [tags, setTags] = useState<string[]>([])
  const [attachment, setAttachment] = useState<ForumAttachmentInput | null>(null)
  const [file, setFile] = useState<ForumFileInput | null>(null)
  const [showPoll, setShowPoll] = useState(false)
  const [pollQuestion, setPollQuestion] = useState('')
  const [pollOptions, setPollOptions] = useState(['', '', ''])
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle')
  const [error, setError] = useState('')

  useEffect(() => {
    fetch('/api/me')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setUser(data?.user ?? null))
      .catch(() => {})
      .finally(() => setLoaded(true))
  }, [])

  const inputClass =
    'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/30'

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setStatus('loading')
    try {
      const res = await fetch('/api/forum/topics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          body,
          category,
          tags,
          attachment,
          file,
          pollQuestion: showPoll ? pollQuestion : '',
          pollOptions: showPoll ? pollOptions : [],
        }),
      })
      const data = (await res.json().catch(() => ({}))) as { error?: string; topicId?: number }
      if (!res.ok) throw new Error(data.error || 'Could not create the topic.')
      window.location.href = `/forum/topic/${data.topicId}`
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the topic.')
      setStatus('error')
    }
  }

  if (!loaded) return null

  if (!user) {
    return (
      <ContentPage wide eyebrow="// forums" title="New topic" description="Start a new forum topic.">
        <p className="text-sm text-muted-foreground">
          Please{' '}
          <a href="/login" className="text-primary underline underline-offset-4">
            log in
          </a>{' '}
          to create a topic.
        </p>
      </ContentPage>
    )
  }

  if (!user.disclaimerAccepted) {
    return (
      <ContentPage wide eyebrow="// forums" title="New topic" description="Start a new forum topic.">
        <p className="text-sm text-muted-foreground">
          You need to accept the{' '}
          <a href="/forum" className="text-primary underline underline-offset-4">
            forum rules
          </a>{' '}
          before posting.
        </p>
      </ContentPage>
    )
  }

  return (
    <ContentPage
      wide
      eyebrow="// forums"
      title="New topic"
      description="Share a question, an idea, or something you've learned."
    >
      <a
        href="/forum"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to forums
      </a>

      <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-4">
        <label htmlFor="topic-title" className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">Title</span>
          <input
            id="topic-title"
            type="text"
            required
            minLength={4}
            maxLength={120}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Programming a UV-5R from macOS"
            className={inputClass}
          />
        </label>

        <label htmlFor="topic-body" className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">Details</span>
          <textarea
            id="topic-body"
            required
            rows={8}
            minLength={10}
            maxLength={10000}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Describe your question or idea. Include radio model, software version, and steps you've tried where relevant."
            className="w-full resize-y rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring/30"
          />
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Category</span>
            <select
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              className={inputClass}
            >
              {forumCategories
                .filter((item) => item.value !== 'announcement' || user.isAdmin)
                .map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
            </select>
          </label>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">Tags</span>
            <div className="flex flex-wrap gap-2">
              {forumTags.map((tag) => {
                const selected = tags.includes(tag)
                return (
                  <button
                    key={tag}
                    type="button"
                    aria-pressed={selected}
                    onClick={() =>
                      setTags((current) =>
                        selected
                          ? current.filter((item) => item !== tag)
                          : current.length < 5
                            ? [...current, tag]
                            : current,
                      )
                    }
                    className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                      selected
                        ? 'border-primary/40 bg-primary/10 text-primary'
                        : 'border-border bg-background text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {tag}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">Image attachment</span>
          <ForumAttachmentPicker
            value={attachment}
            onChange={setAttachment}
            disabled={status === 'loading'}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">Channel file</span>
          <ForumFilePicker value={file} onChange={setFile} disabled={status === 'loading'} />
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <button
            type="button"
            onClick={() => setShowPoll((value) => !value)}
            className="text-sm font-medium text-foreground"
          >
            {showPoll ? 'Remove poll' : 'Add a poll'}
          </button>
          {showPoll && (
            <div className="mt-3 flex flex-col gap-3">
              <input
                type="text"
                maxLength={160}
                value={pollQuestion}
                onChange={(event) => setPollQuestion(event.target.value)}
                placeholder="Poll question"
                className={inputClass}
              />
              {pollOptions.map((option, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength={100}
                  value={option}
                  onChange={(event) =>
                    setPollOptions((current) =>
                      current.map((item, itemIndex) =>
                        itemIndex === index ? event.target.value : item,
                      ),
                    )
                  }
                  placeholder={`Option ${index + 1}`}
                  className={inputClass}
                />
              ))}
              {pollOptions.length < 6 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="self-start"
                  onClick={() => setPollOptions((current) => [...current, ''])}
                >
                  Add option
                </Button>
              )}
            </div>
          )}
        </div>

        {status === 'error' && error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={status === 'loading'}>
            {status === 'loading' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Create topic
          </Button>
        </div>
      </form>
    </ContentPage>
  )
}
